export type WeatherFeatureServerLayer = {
  id: string;
  name: string;
  /** Grouping shown in the picker, e.g. "Watches and warnings". */
  category: string;
  description: string;
  serviceUrl: string;
  /**
   * Portal item id, so the Layers tab seed dedupes against hazard portal
   * entries.
   */
  arcgisItemId?: string;
  /** Stable per-feature id field, copied onto a rule source when picked. */
  featureIdField?: string;
  /**
   * `asset` opens one event per impacted asset instead of one per feature, for
   * dense layers (hotspots, gridded forecasts) where features are not
   * incidents.
   */
  occurrenceBy?: 'feature' | 'asset';
  /**
   * Has a dedicated report Analytics pack, so it can be picked on an Analytics
   * section and is pinned first in the rule feed picker.
   */
  analytics?: boolean;
  /**
   * Kept for the Layers tab and for rules already on it, but not offered in the
   * rule feed or Analytics pickers.
   */
  hiddenFromPickers?: boolean;
};

/**
 * Public Living Atlas FeatureServer layers (NOAA, NWS, USGS, NASA, EPA) offered
 * when a rule points at a custom ArcGIS feed, so an author does not have to
 * paste a URL by hand.
 *
 * Every entry is a layer URL rather than a service root: the field picker reads
 * its schema straight from this URL and cannot resolve a root on its own.
 */
export const WEATHER_FEATURESERVER_CATALOG: WeatherFeatureServerLayer[] = [
  {
    id: 'nws-events-by-severity',
    name: 'NWS events by size and severity',
    category: 'Watches and warnings',
    description: 'All active NWS watches, warnings, and advisories.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/NWS_Watches_Warnings_v1/FeatureServer/6',
  },
  {
    id: 'nws-extreme-events',
    name: 'NWS extreme events',
    category: 'Watches and warnings',
    description: 'Active NWS events classified as extreme severity.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/NWS_Watches_Warnings_v1/FeatureServer/8',
  },
  {
    id: 'nws-severe-events',
    name: 'NWS severe events',
    category: 'Watches and warnings',
    description: 'Active NWS events classified as severe severity.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/NWS_Watches_Warnings_v1/FeatureServer/9',
  },
  {
    id: 'hurricanes-forecast-cone',
    name: 'Hurricane forecast cone',
    category: 'Tropical',
    description: 'NHC forecast error cone and danger area polygons.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/Active_Hurricanes_v1/FeatureServer/4',
  },
  {
    id: 'hurricanes-forecast-position',
    name: 'Hurricane forecast position',
    category: 'Tropical',
    description: 'NHC forecast track points with wind speed and category.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/Active_Hurricanes_v1/FeatureServer/0',
  },
  {
    id: 'hurricanes-observed-position',
    name: 'Hurricane observed position',
    category: 'Tropical',
    description: 'NHC observed storm positions to date.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/Active_Hurricanes_v1/FeatureServer/1',
  },
  {
    id: 'hurricanes-wind-probability-34kt',
    analytics: true,
    name: 'Tropical-storm-force wind probability (34 kt)',
    category: 'Tropical',
    description:
      'NHC 120-hour probability of tropical-storm-force winds (PWIND120, percent).',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/Active_Hurricanes_v1/FeatureServer/7',
    occurrenceBy: 'asset',
  },
  {
    id: 'hurricanes-wind-probability-50kt',
    analytics: true,
    name: 'Strong tropical-storm wind probability (50 kt)',
    category: 'Tropical',
    description: 'NHC 120-hour probability of 50 kt winds (PWIND120, percent).',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/Active_Hurricanes_v1/FeatureServer/8',
    occurrenceBy: 'asset',
  },
  {
    id: 'hurricanes-wind-probability-64kt',
    analytics: true,
    name: 'Hurricane-force wind probability (64 kt)',
    category: 'Tropical',
    description:
      'NHC 120-hour probability of hurricane-force winds (PWIND120, percent).',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/Active_Hurricanes_v1/FeatureServer/9',
    occurrenceBy: 'asset',
  },
  {
    id: 'ndfd-wind-gust',
    analytics: true,
    name: 'Forecast wind gust',
    category: 'Wind',
    description: 'NDFD forecast wind gust bands by forecast period (US).',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/NDFD_WindGust_v1/FeatureServer/0',
    occurrenceBy: 'asset',
  },
  {
    id: 'ndfd-precipitation-total',
    name: 'Forecast precipitation total',
    category: 'Precipitation and water',
    description: 'NDFD cumulative forecast precipitation amounts.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/NDFD_Precipitation_v1/FeatureServer/2',
  },
  {
    id: 'live-stream-gauges',
    name: 'Live stream gauges',
    category: 'Precipitation and water',
    description: 'USGS/NWS river gauges with live stage and flow readings.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/Live_Stream_Gauges_v1/FeatureServer/0',
  },
  {
    id: 'wildfire-perimeters',
    name: 'Current wildfire perimeters',
    category: 'Fire',
    description: 'Active US wildfire perimeters with acreage attributes.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/USA_Wildfires_v1/FeatureServer/1',
  },
  {
    id: 'wildfire-incidents',
    analytics: true,
    name: 'Current wildfire incidents',
    category: 'Fire',
    description: 'Active US wildfire incident points with containment detail.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/USA_Wildfires_v1/FeatureServer/0',
  },
  {
    id: 'viirs-thermal-hotspots',
    analytics: true,
    name: 'Satellite (VIIRS) thermal hotspots',
    category: 'Fire',
    description:
      'Global VIIRS satellite fire detections with confidence and fire radiative power.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/Satellite_VIIRS_Thermal_Hotspots_and_Fire_Activity/FeatureServer/0',
    arcgisItemId: 'dece90af1a0242dcbf0ca36d30276aa3',
    occurrenceBy: 'asset',
  },
  {
    id: 'usgs-earthquakes',
    analytics: true,
    name: 'Earthquakes (USGS, global)',
    category: 'Earthquakes',
    description:
      'Global USGS earthquake events with magnitude, PAGER alert, and tsunami flag.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/USGS_Seismic_Data_v1/FeatureServer/0',
    featureIdField: 'id',
  },
  {
    id: 'usgs-shake-intensity',
    analytics: true,
    name: 'Earthquake shake intensity (USGS ShakeMap)',
    category: 'Earthquakes',
    description:
      'ShakeMap intensity polygons (MMI) for significant recent earthquakes.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/USGS_Seismic_Data_v1/FeatureServer/1',
    featureIdField: 'uniqueId',
  },
  {
    id: 'airnow-current-monitors',
    analytics: true,
    name: 'Current air quality (AirNow)',
    category: 'Air quality',
    description:
      'EPA AirNow monitor readings, including PM2.5 and ozone concentration and AQI.',
    serviceUrl:
      'https://services.arcgis.com/cJ9YHowT8TU7DUyn/arcgis/rest/services/Air%20Now%20Current%20Monitor%20Data%20Public/FeatureServer/0',
    featureIdField: 'AQSID',
  },
  {
    id: 'usda-wild-bird-avian-influenza',
    analytics: true,
    name: 'Avian influenza in wild birds (USDA APHIS)',
    category: 'Animal health',
    description:
      'APHIS wild bird surveillance samples at county centroids, with influenza A, H5, H7, and high-path results.',
    serviceUrl:
      'https://services7.arcgis.com/2C1NQ7u6M6SXoa8p/arcgis/rest/services/VS_Avian_Influenza_Wild_Bird_Surveillance_Dashboard_data_view_feature_layer_/FeatureServer/0',
    arcgisItemId: '68993d1871974545a6f9630c599575f4',
    occurrenceBy: 'asset',
  },
  {
    id: 'metar-stations',
    analytics: true,
    name: 'Current weather stations (NOAA METAR)',
    category: 'Current conditions',
    description:
      'Airport and surface station observations: temperature, wind, gusts, visibility, and weather.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/NOAA_METAR_current_wind_speed_direction_v1/FeatureServer/0',
    featureIdField: 'ICAO',
  },
  {
    id: 'nws-storm-reports',
    analytics: true,
    name: 'Storm reports, past day (NWS)',
    category: 'Current conditions',
    description:
      'Confirmed tornado, hail, wind, and flood reports from the past day.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/NOAA_storm_reports_v1/FeatureServer/4',
    occurrenceBy: 'asset',
  },
  {
    id: 'ndfd-daily-temperature-max',
    analytics: true,
    name: 'Daily high temperature forecast (NWS)',
    category: 'Temperature',
    description: 'NDFD forecast maximum temperature for the next three days.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/NDFD_DailyTemperature_v1/FeatureServer/1',
    occurrenceBy: 'asset',
  },
  {
    id: 'ndfd-daily-temperature-min',
    analytics: true,
    name: 'Daily low temperature forecast (NWS)',
    category: 'Temperature',
    description: 'NDFD forecast minimum temperature for the next three days.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/NDFD_DailyTemperature_v1/FeatureServer/0',
    occurrenceBy: 'asset',
  },
  {
    id: 'ndfd-snowfall-total',
    analytics: true,
    name: 'Snowfall forecast (NWS)',
    category: 'Winter weather',
    description: 'NDFD cumulative snowfall for the forecast period.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/NDFD_SnowFall_v1/FeatureServer/2',
    occurrenceBy: 'asset',
  },
  {
    id: 'ndfd-ice-total',
    analytics: true,
    name: 'Ice accumulation forecast (NWS)',
    category: 'Winter weather',
    description: 'NDFD cumulative ice accumulation for the forecast period.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/NDFD_Ice_v1/FeatureServer/2',
    occurrenceBy: 'asset',
  },
  {
    id: 'spc-categorical-day-1',
    hiddenFromPickers: true,
    name: 'Severe storm outlook, day 1 (SPC)',
    category: 'Severe storms',
    description:
      'Storm Prediction Center day-1 categorical outlook, from thunderstorms to high risk.',
    serviceUrl:
      'https://mapservices.weather.noaa.gov/vector/rest/services/outlooks/SPC_wx_outlks/FeatureServer/1',
  },
  {
    id: 'spc-categorical-day-2',
    hiddenFromPickers: true,
    name: 'Severe storm outlook, day 2 (SPC)',
    category: 'Severe storms',
    description: 'Storm Prediction Center day-2 categorical outlook.',
    serviceUrl:
      'https://mapservices.weather.noaa.gov/vector/rest/services/outlooks/SPC_wx_outlks/FeatureServer/9',
  },
  {
    id: 'spc-categorical-day-3',
    hiddenFromPickers: true,
    name: 'Severe storm outlook, day 3 (SPC)',
    category: 'Severe storms',
    description: 'Storm Prediction Center day-3 categorical outlook.',
    serviceUrl:
      'https://mapservices.weather.noaa.gov/vector/rest/services/outlooks/SPC_wx_outlks/FeatureServer/17',
  },
  {
    id: 'wpc-excessive-rainfall-day-1',
    hiddenFromPickers: true,
    name: 'Excessive rainfall outlook, day 1 (WPC)',
    category: 'Severe storms',
    description:
      'Weather Prediction Center day-1 chance of rainfall exceeding flash-flood guidance.',
    serviceUrl:
      'https://mapservices.weather.noaa.gov/vector/rest/services/hazards/wpc_precip_hazards/MapServer/0',
  },
  {
    id: 'wpc-excessive-rainfall-day-2',
    hiddenFromPickers: true,
    name: 'Excessive rainfall outlook, day 2 (WPC)',
    category: 'Severe storms',
    description: 'Weather Prediction Center day-2 excessive rainfall outlook.',
    serviceUrl:
      'https://mapservices.weather.noaa.gov/vector/rest/services/hazards/wpc_precip_hazards/MapServer/1',
  },
  {
    id: 'wpc-excessive-rainfall-day-3',
    hiddenFromPickers: true,
    name: 'Excessive rainfall outlook, day 3 (WPC)',
    category: 'Severe storms',
    description: 'Weather Prediction Center day-3 excessive rainfall outlook.',
    serviceUrl:
      'https://mapservices.weather.noaa.gov/vector/rest/services/hazards/wpc_precip_hazards/MapServer/2',
  },
  {
    id: 'wssi-overall-impact',
    hiddenFromPickers: true,
    name: 'Winter storm severity (WPC)',
    category: 'Winter weather',
    description:
      'Winter Storm Severity Index overall impact for the next three days.',
    serviceUrl:
      'https://mapservices.weather.noaa.gov/vector/rest/services/outlooks/wpc_wssi/MapServer/4',
    occurrenceBy: 'asset',
  },
];

export function normalizeWeatherServiceUrl(serviceUrl: string): string {
  return serviceUrl.trim().replace(/\/+$/, '');
}

export function findWeatherLayerByUrl(
  serviceUrl: string | null | undefined
): WeatherFeatureServerLayer | undefined {
  if (!serviceUrl) return undefined;
  const normalized = normalizeWeatherServiceUrl(serviceUrl);
  return WEATHER_FEATURESERVER_CATALOG.find(
    (layer) => normalizeWeatherServiceUrl(layer.serviceUrl) === normalized
  );
}

/**
 * Source fields a catalog layer implies. Non-catalog URLs clear them so a
 * previously picked layer's id field does not leak onto a custom feed.
 */
export function catalogSourceDefaults(serviceUrl: string | null | undefined): {
  featureIdField: string | undefined;
  occurrenceBy: 'feature' | 'asset' | undefined;
} {
  const layer = findWeatherLayerByUrl(serviceUrl);
  return {
    featureIdField: layer?.featureIdField,
    occurrenceBy: layer?.occurrenceBy,
  };
}

export const ANALYTICS_READY_GROUP_LABEL = 'Analytics-ready hazards';

/** Catalog layers that report Analytics sections can read. */
export function analyticsLayers(): WeatherFeatureServerLayer[] {
  return WEATHER_FEATURESERVER_CATALOG.filter((layer) => layer.analytics);
}

export function findWeatherLayerById(
  id: string | null | undefined
): WeatherFeatureServerLayer | undefined {
  if (!id) return undefined;
  return WEATHER_FEATURESERVER_CATALOG.find((layer) => layer.id === id);
}

/**
 * Catalog entries grouped by category, in first-seen order. With
 * `pinAnalytics`, analytics layers move into a leading group instead, so each
 * URL still appears once.
 */
export function weatherLayersByCategory(options?: { pinAnalytics?: boolean }): {
  label: string;
  items: string[];
}[] {
  const groups: { label: string; items: string[] }[] = [];
  if (options?.pinAnalytics) {
    groups.push({
      label: ANALYTICS_READY_GROUP_LABEL,
      items: analyticsLayers().map((layer) => layer.serviceUrl),
    });
  }
  for (const layer of WEATHER_FEATURESERVER_CATALOG) {
    if (options?.pinAnalytics && layer.analytics) continue;
    const existing = groups.find((group) => group.label === layer.category);
    if (existing) {
      existing.items.push(layer.serviceUrl);
    } else {
      groups.push({ label: layer.category, items: [layer.serviceUrl] });
    }
  }
  return groups;
}
